import { getTaxIdentifierLabel, validateTaxIdentifier } from '@/utils/tax-identifier'

export function normalizeCountryCode(country) {
  return typeof country === 'string' ? country.trim().toUpperCase() : ''
}

export function isUsCountry(country) {
  return normalizeCountryCode(country) === 'US'
}

function hasValue(value) {
  return value !== null && value !== undefined && String(value).trim() !== ''
}

export function createRequiredFieldRule(message, trigger = 'change') {
  return {
    required: true,
    validator: (_, value) => {
      if (hasValue(value)) {
        return Promise.resolve()
      }

      return Promise.reject(new Error(message))
    },
    trigger
  }
}

export function createConditionalRequiredFieldRule(predicate, message, trigger = 'change') {
  return {
    validator: (_, value) => {
      if (!predicate()) {
        return Promise.resolve()
      }

      if (hasValue(value)) {
        return Promise.resolve()
      }

      return Promise.reject(new Error(message))
    },
    trigger
  }
}

export function createRequiredTaxIdentifierRule(getIsSsn, trigger = 'change') {
  return {
    required: true,
    validator: (_, value) => {
      const label = getTaxIdentifierLabel(getIsSsn())

      if (!hasValue(value)) {
        return Promise.reject(new Error(`Please enter ${label}.`))
      }

      const validationMessage = validateTaxIdentifier(value, getIsSsn())

      if (validationMessage) {
        return Promise.reject(new Error(validationMessage))
      }

      return Promise.resolve()
    },
    trigger
  }
}

export function createConditionalRequiredTaxIdentifierRule(predicate, getIsSsn, trigger = 'change') {
  return {
    validator: (_, value) => {
      if (!predicate()) {
        return Promise.resolve()
      }

      const label = getTaxIdentifierLabel(getIsSsn())

      if (!hasValue(value)) {
        return Promise.reject(new Error(`Please enter ${label}.`))
      }

      const validationMessage = validateTaxIdentifier(value, getIsSsn())

      if (validationMessage) {
        return Promise.reject(new Error(validationMessage))
      }

      return Promise.resolve()
    },
    trigger
  }
}

export function getAssociateProfileCompletionErrors(profile = {}) {
  const address = profile.address || {}
  const errors = []
  const taxLabel = getTaxIdentifierLabel(profile.is_ssn)
  const taxIdentifier = profile.social_security_number

  if (!hasValue(taxIdentifier)) {
    errors.push(`Add ${taxLabel}.`)
  } else {
    const taxValidationMessage = validateTaxIdentifier(taxIdentifier, profile.is_ssn)

    if (taxValidationMessage) {
      errors.push(taxValidationMessage)
    }
  }

  if (!profile.birth_date) {
    errors.push('Add birth date.')
  }

  if (!hasValue(address.address_line1)) {
    errors.push('Add address line 1.')
  }

  if (!hasValue(address.city)) {
    errors.push('Add city.')
  }

  if (!hasValue(address.country)) {
    errors.push('Add country.')
  }

  if (isUsCountry(address.country) && !hasValue(address.state)) {
    errors.push('Add state for a US address.')
  }

  if (isUsCountry(address.country) && !hasValue(address.zipcode)) {
    errors.push('Add ZIP code for a US address.')
  }

  return errors
}

export function getAssociateProfileCompletionErrorMessage(profile = {}) {
  const errors = getAssociateProfileCompletionErrors(profile)

  if (!errors.length) {
    return ''
  }

  return ['Complete the associate profile before continuing.', ...errors].join(' ')
}

export function hasCompleteAssociateProfile(profile = {}) {
  return getAssociateProfileCompletionErrors(profile).length === 0
}
